import * as z from 'zod'

import {
  CreatePermissionTemplateVars,
  UpdatePermissionTemplateVars
} from '@/common/api/params/permissionTemplate'
import { NameLabelMapping } from '@/common/app/message'
import { StringLength } from '@/common/form/schema'
import { PermissionTemplate } from '@/common/model/auth'

// メニュー利用権限のキーと表示ラベル（バックエンドの menu_permissions キーと一致させる）
export const PERMISSION_MENU_KEYS = [
  { key: 'reco_media', label: 'AIレコメンド（Reco-Media）' },
  // 2026-10-06 追加: Reco-Media のオプション。Reco-Media の権限と両方あるときに使える
  { key: 'mail_reco', label: 'メールレコメンド（Reco-Media オプション）' },
  { key: 'content_analysis', label: 'コンテンツ解析（Media-DRIVER）' },
  { key: 'geo_llmo', label: 'GEO/LLMO最適化' },
  { key: 'review', label: 'レビュー / UGC' },
  { key: 'ai_purchase_assist', label: 'AI購入支援' },
  { key: 'search_suggest', label: '検索サジェスト（Findee）' },
  { key: 'search_tag', label: 'Tag Trail' },
  { key: 'ai_persona', label: 'AIペルソナ' },
  { key: 'article_upload', label: '記事アップロード' }
] as const

export const permissionTemplateSchema = z.object({
  id: z.number().readonly().optional(),
  isSystem: z.boolean().optional(),
  name: z.string().min(1).max(StringLength.NORMAL_MAX),
  menuPermissions: z.object({
    reco_media: z.boolean(),
    mail_reco: z.boolean(),
    content_analysis: z.boolean(),
    geo_llmo: z.boolean(),
    review: z.boolean(),
    ai_purchase_assist: z.boolean(),
    search_suggest: z.boolean(),
    search_tag: z.boolean(),
    ai_persona: z.boolean(),
    article_upload: z.boolean()
  }),
  canApproveArticle: z.boolean()
})

export type PermissionTemplateFormSchemaType = z.infer<typeof permissionTemplateSchema>

export const convertPermissionTemplateSchemaToVars = (
  siteId: number,
  schema: PermissionTemplateFormSchemaType
): CreatePermissionTemplateVars | UpdatePermissionTemplateVars => {
  const base = {
    name: schema.name,
    menu_permissions: schema.menuPermissions,
    can_approve_article: schema.canApproveArticle
  }
  if (schema.id !== undefined) {
    return { site_id: siteId, template_id: schema.id, ...base }
  }
  return { site_id: siteId, ...base }
}

export const convertPermissionTemplateToSchema = (
  template: PermissionTemplate
): PermissionTemplateFormSchemaType => {
  const mp = template.menu_permissions ?? {}
  return {
    id: template.id,
    isSystem: template.is_system,
    name: template.name,
    menuPermissions: {
      reco_media: !!mp.reco_media,
      mail_reco: !!mp.mail_reco,
      content_analysis: !!mp.content_analysis,
      geo_llmo: !!mp.geo_llmo,
      review: !!mp.review,
      ai_purchase_assist: !!mp.ai_purchase_assist,
      search_suggest: !!mp.search_suggest,
      search_tag: !!mp.search_tag,
      ai_persona: !!mp.ai_persona,
      article_upload: !!mp.article_upload
    },
    canApproveArticle: template.can_approve_article
  }
}

export const DEFAULT_PERMISSION_TEMPLATE_SCHEMA: PermissionTemplateFormSchemaType = {
  name: '',
  menuPermissions: {
    reco_media: false,
    mail_reco: false,
    content_analysis: false,
    geo_llmo: false,
    review: false,
    ai_purchase_assist: false,
    search_suggest: false,
    search_tag: false,
    ai_persona: false,
    article_upload: false
  },
  canApproveArticle: false
}

export const permissionTemplateNameLabelMapping: NameLabelMapping = {
  name: 'テンプレート名'
}
